import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog as SheetPrimitive } from "radix-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type ProductReview, type ProductReviewList } from "@/api";
import { HeartFilledIcon, HeartLine16Icon, SortIcon, StarIcon, XIcon } from "@/components/icons";
import { useProfile } from "@/store";
import { Chip } from "@/components/ui/chip";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@osmix/ui/skeleton";
import { cn } from "@/lib/utils";
import { ProductQuestionsPane, useProductQuestions } from "./product-questions";
export type ProductSocialTab = "reviews" | "questions";
const formatDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric"
});

// 1 отзыв / 2 отзыва / 5 отзывов.
const plural = (n: number, [one, few, many]: [string, string, string]) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};
type Sort = "newest" | "best" | "worst";
const SORTS: ReadonlyArray<[Sort, string]> = [["newest", "Сначала новые"], ["best", "Сначала хорошие"], ["worst", "Сначала негативные"]];

// Цвет из подписи варианта отзыва («Чёрный, 46» → «чёрный») — для чипов.
const reviewColor = (r: ProductReview) => (r.variant ?? "").split(",")[0]?.trim().toLowerCase() ?? "";

/*
 * Отзывы о товаре — левая панель по кадру «Отзывы» (3869:19619): 610px на
 * #F8F8F8 поверх затемнения 20 % с размытием 3px. Открывается ссылкой «N оценок»
 * в рейтинг-строке карточки. Вопросы к товару (fd-review-02/03, сняты 01.10 и
 * возвращены 31.10) - второй вкладкой той же панели: «Отзывы · Вопросы»
 * под заголовком, тем же видом, что вкладки карточки; макет отзывов не
 * меняется. Ссылка «Вопросы (N)» в рейтинг-строке открывает панель сразу на
 * вопросах.
 *
 * Шапка: «бренд название» 20/23 и крестик. Белая карточка r16: чипы «Все
 * отзывы» + цвета товара, встречающиеся в отзывах; средняя 32/34 со звёздами и
 * «продано / отзывов / возвратов» (числа шлюза из сводки). Тумблер «только с
 * фото и видео», сортировка «новые / хорошие / негативные». Отзыв — белая
 * карточка r16: имя и дата, фото 80×106 r4, звёзды 16 и вариант, текст и
 * «Ответ:» магазина с отступом 16.
 */
export function ProductReviewsPanel({
  productId,
  title,
  colors,
  open,
  onOpenChange,
  tab = "reviews",
  onTabChange
}: {
  productId: string;
  title: string;
  /** Цвета вариантов товара — из них собираются чипы фильтра. */
  colors: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Вкладка панели: отзывы (по умолчанию) или вопросы. */
  tab?: ProductSocialTab;
  onTabChange?: (tab: ProductSocialTab) => void;
}) {
  const {
    t
  } = useTranslation();
  const questionCount = useProductQuestions(productId, open).data?.items.length;
  // Тот же ключ, что у рейтинг-строки карточки: сводка и лента — один запрос.
  const reviews = useQuery({
    queryKey: ["products", "reviews", productId],
    queryFn: () => api.products.reviews(productId),
    enabled: open
  });
  const [color, setColor] = useState<string | null>(null);
  const [withPhotos, setWithPhotos] = useState(false);
  const [sort, setSort] = useState<Sort>("newest");
  const [sortOpen, setSortOpen] = useState(false);
  const items = reviews.data?.items ?? [];
  const summary = reviews.data?.summary;

  // Чипы — только цвета товара, о которых реально есть отзывы.
  const colorChips = useMemo(() => {
    const inReviews = new Set(items.map(reviewColor).filter(Boolean));
    const seen = new Set<string>();
    return colors.filter(c => {
      const key = c.trim().toLowerCase();
      if (!key || seen.has(key) || !inReviews.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [colors, items]);
  const visible = useMemo(() => {
    let list = items;
    if (color) list = list.filter(r => reviewColor(r) === color.trim().toLowerCase());
    if (withPhotos) list = list.filter(r_0 => r_0.photoUrls.length > 0);
    const byDate = (a: ProductReview, b: ProductReview) => b.createdAt.localeCompare(a.createdAt);
    return [...list].sort((a_0, b_0) => sort === "best" ? b_0.rating - a_0.rating || byDate(a_0, b_0) : sort === "worst" ? a_0.rating - b_0.rating || byDate(a_0, b_0) : byDate(a_0, b_0));
  }, [items, color, withPhotos, sort]);
  return <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<SheetPrimitive.Portal>
				<SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[3px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
				<SheetPrimitive.Content data-testid="product-reviews-panel" className="fixed inset-y-0 left-0 z-50 flex w-full max-w-[610px] flex-col overflow-y-auto bg-[#F8F8F8] pb-5 outline-none data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=open]:duration-500">
					<header className="flex items-start gap-6 pt-[30px] pr-5 pb-2.5 pl-4 sm:pl-[30px]">
						<SheetPrimitive.Title className="min-w-0 flex-1 text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#2E2E2E]">
							{title}
						</SheetPrimitive.Title>
						<SheetPrimitive.Description className="sr-only">{t("product.qa.panelTitle")}</SheetPrimitive.Description>
						<SheetPrimitive.Close aria-label="Закрыть" className="grid size-6 shrink-0 place-items-center rounded-[4px] text-[#1F1F1F] outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
							<XIcon />
						</SheetPrimitive.Close>
					</header>

					{/* «Отзывы · Вопросы» - вкладки как у карточки товара (16/21, активная
         w600 с линией 1px). */}
					<div role="tablist" aria-label={t("product.qa.panelTitle")} className="mr-5 ml-4 flex gap-6 border-b border-[#1F1F1F]/8 sm:ml-[30px]">
						{(["reviews", "questions"] as const).map(key_0 => <button key={key_0} type="button" role="tab" aria-selected={tab === key_0} data-testid={`social-tab-${key_0}`} onClick={() => onTabChange?.(key_0)} className={cn("relative h-8 shrink-0 whitespace-nowrap pt-1.5 text-[16px] leading-[21px] outline-none transition-colors focus-visible:text-osmix-tangelo", tab === key_0 ? "font-semibold text-[#1F1F1F] after:absolute after:inset-x-0 after:bottom-0 after:h-px after:rounded-full after:bg-[#1F1F1F]" : "font-medium text-[#1F1F1F]/48 hover:text-[#1F1F1F]")}>
								{key_0 === "reviews" ? t("product.qa.reviewsTab", {
              count: summary?.count ?? 0
            }) : t("product.qa.questionsTab", {
              count: questionCount ?? 0
            })}
							</button>)}
					</div>

					{tab === "questions" ? <ProductQuestionsPane productId={productId} /> : reviews.isPending ? <div className="flex flex-col gap-2.5 px-5 py-2.5 sm:pl-[30px]">
							<Skeleton className="h-[132px] rounded-[16px] bg-white" />
							<Skeleton className="h-[190px] rounded-[16px] bg-white" />
						</div> : reviews.isError || !summary ? <p className="px-5 py-2.5 text-[14px] text-black/50 sm:pl-[30px]">Не удалось загрузить отзывы</p> : <>
							<div className="py-2.5 pr-5 pl-4 sm:pl-[30px]">
								<div className="flex flex-col gap-4 rounded-[16px] bg-white px-3 py-4">
									<div className="flex flex-wrap gap-2.5">
										<Chip selected={color == null} onClick={() => setColor(null)}>
											Все отзывы
										</Chip>
										{colorChips.map(c_0 => <Chip key={c_0} selected={color === c_0} onClick={() => setColor(color === c_0 ? null : c_0)}>
												{c_0}
											</Chip>)}
									</div>
									<div className="flex items-end gap-3">
										<div className="flex w-[68px] flex-col items-center gap-1 pb-0.5">
											<span className="text-[32px] leading-[34px] font-semibold tracking-[-0.31px] text-black">
												{summary.average.toFixed(1)}
											</span>
											<StarsRow value={summary.average} size={12} className="text-[#FA5C2E]" />
										</div>
										<Stat value={summary.soldCount} label="продано" />
										<Stat value={summary.count} label={plural(summary.count, ["отзыв", "отзыва", "отзывов"])} />
										<Stat value={summary.returnCount} label={plural(summary.returnCount, ["возврат", "возврата", "возвратов"])} />
									</div>
								</div>
							</div>

							<div className="flex items-center gap-2.5 py-2.5 pr-5 pl-4 sm:pl-[30px]">
								{/* Переключатель DS 36×20 (включён #FF5E00) с подписью 16/21 w500. */}
								<label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-[16px] leading-[21px] font-medium text-ds-primary">
									<Switch checked={withPhotos} onCheckedChange={setWithPhotos} />
									показывать только отзывы с фото и видео
								</label>
								<Popover open={sortOpen} onOpenChange={setSortOpen}>
									<PopoverTrigger asChild>
										<button type="button" aria-label="Сортировка отзывов" className="grid size-8 shrink-0 place-items-center rounded-[8px] text-ds-primary outline-none hover:bg-ds-hover focus-visible:ring-2 focus-visible:ring-ds-focus">
											<SortIcon />
										</button>
									</PopoverTrigger>
									<PopoverContent align="end" sideOffset={4} className="z-[60] w-auto rounded-[10px] border-0 p-[3px] shadow-[var(--ds-shadow-dropdown)]">
										{SORTS.map(([value, label]) => <button key={value} type="button" onClick={() => {
                  setSort(value);
                  setSortOpen(false);
                }} className={cn("flex h-[39px] w-full items-center rounded-[8px] px-3 text-left text-[14px] leading-[19px] font-medium whitespace-nowrap text-ds-primary outline-none hover:bg-ds-hover focus-visible:bg-ds-hover", sort === value && "bg-ds-hover")}>
												{label}
											</button>)}
									</PopoverContent>
								</Popover>
							</div>

							{summary.count === 0 ? <p className="px-5 py-2.5 text-[14px] text-black/50 sm:pl-[30px]">У товара пока нет отзывов</p> : visible.length === 0 ? <p className="px-5 py-2.5 text-[14px] text-black/50 sm:pl-[30px]">По выбранным фильтрам отзывов нет</p> : <ul className="flex flex-col">
									{visible.map(review => <li key={review.id} className="py-2.5 pr-5 pl-4 sm:pl-[30px]">
											<ReviewCard review={review} productId={productId} />
										</li>)}
								</ul>}
						</>}
				</SheetPrimitive.Content>
			</SheetPrimitive.Portal>
		</SheetPrimitive.Root>;
}

/** Лайк отзыва на витрине. Выключен до следующей версии (№4, 19.10.2026). */
const REVIEW_LIKES_ENABLED = false;
function ReviewCard(t0) {
  const $ = _c(36);
  const {
    review,
    productId
  } = t0;
  let t1;
  if ($[0] !== review.authorName) {
    t1 = <span className="text-[14px] leading-[18px] font-medium text-[#1F1F1F]">{review.authorName}</span>;
    $[0] = review.authorName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== review.createdAt) {
    t2 = formatDate(review.createdAt);
    $[2] = review.createdAt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-[12px] leading-[18px] text-black/50">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <p className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">{t1}{t3}</p>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== productId || $[10] !== review) {
    t5 = REVIEW_LIKES_ENABLED && <ReviewLike review={review} productId={productId} />;
    $[9] = productId;
    $[10] = review;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex items-start gap-3">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== review.photoUrls) {
    t7 = review.photoUrls.length > 0 && <div className="flex gap-1 overflow-x-auto [scrollbar-width:none]">{review.photoUrls.map(_temp3)}</div>;
    $[15] = review.photoUrls;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== review.rating) {
    t8 = <StarsRow value={review.rating} size={16} className="text-[#1F1F1F]" />;
    $[17] = review.rating;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== review.variant) {
    t9 = review.variant && <span className="text-[14px] leading-[18px] text-[#1F1F1F]">{review.variant}</span>;
    $[19] = review.variant;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="flex flex-wrap items-center gap-1">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== review.text) {
    t11 = review.text && <p className="text-[14px] leading-[18px] whitespace-pre-line break-words text-black">{review.text}</p>;
    $[24] = review.text;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <div className="flex flex-col gap-1.5">{t10}{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== review.sellerReply) {
    t13 = review.sellerReply && <div className="flex flex-col gap-1 pl-4" data-testid="review-seller-reply"><span className="text-[14px] leading-[18px] text-black/50">Ответ:</span><p className="text-[14px] leading-[18px] whitespace-pre-line break-words text-black">{review.sellerReply}</p></div>;
    $[29] = review.sellerReply;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t6 || $[34] !== t7) {
    t14 = <article className="flex flex-col gap-4 rounded-[16px] bg-white p-2.5" data-testid="product-review">{t6}{t7}{t12}{t13}</article>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t6;
    $[34] = t7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}

/*
 * Лайк отзыва — «сердечко + число» (Айталы 14.10, photo_101: у продавца в
 * таблице отзывов есть столбец «Лайки», у покупателя отметить отзыв было
 * нечем). Один голос покупателя на отзыв, повторное нажатие снимает. Число —
 * тот же счётчик, что видит продавец. Гостю предлагаем войти: голос без
 * аккаунта не к чему привязать.
 */
function _temp3(src) {
  return <a key={src} href={src} target="_blank" rel="noreferrer" className="block shrink-0 overflow-hidden rounded-[4px]"><img src={src} alt="" loading="lazy" className="h-[106px] w-20 object-cover" /></a>;
}
function ReviewLike({
  review,
  productId
}: {
  review: ProductReview;
  productId: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const token = useProfile(s => s.token);
  const [busy, setBusy] = useState(false);
  const liked = review.likedByMe === true;
  const likes = review.likes ?? 0;
  const patch = (next: {
    liked: boolean;
    likes: number;
  }) => queryClient.setQueryData<ProductReviewList>(["products", "reviews", productId], prev => prev ? {
    ...prev,
    items: prev.items.map(item => item.id === review.id ? {
      ...item,
      likedByMe: next.liked,
      likes: next.likes
    } : item)
  } : prev);
  const toggle = async () => {
    if (!token) {
      toast("Войдите, чтобы отмечать отзывы", {
        action: {
          label: "Войти",
          onClick: () => navigate("/profile")
        }
      });
      return;
    }
    if (busy) return;
    const next_0 = !liked;
    // Сразу показываем нажатие, ответ шлюза уточняет число.
    patch({
      liked: next_0,
      likes: Math.max(0, likes + (next_0 ? 1 : -1))
    });
    setBusy(true);
    try {
      patch(await api.buyer.setReviewLiked(review.id, next_0));
    } catch (e) {
      patch({
        liked,
        likes
      });
      toast.error(e instanceof Error ? e.message : "Не удалось отметить отзыв");
    } finally {
      setBusy(false);
    }
  };
  return <button type="button" onClick={() => void toggle()} aria-pressed={liked} aria-label={liked ? "Убрать отметку «нравится»" : "Отзыв нравится"} data-testid="review-like" className={cn("-my-1 flex h-[26px] shrink-0 items-center gap-1 rounded-[8px] px-1.5 text-[14px] leading-[18px] font-medium outline-none transition-colors hover:bg-ds-hover focus-visible:ring-2 focus-visible:ring-ds-focus", liked ? "text-[#FA5C2E]" : "text-[#1F1F1F]")}>
			{liked ? <HeartFilledIcon size={16} /> : <HeartLine16Icon />}
			<span className="min-w-[1ch] tabular-nums">{likes}</span>
		</button>;
}

// Звёзды с дробной долей: серая подложка + залитая маска по проценту.
function StarsRow(t0) {
  const $ = _c(18);
  const {
    value,
    size,
    className
  } = t0;
  const pct = Math.max(0, Math.min(100, value / 5 * 100));
  let t1;
  if ($[0] !== size) {
    t1 = filled => [0, 1, 2, 3, 4].map(i => <StarIcon key={i} size={size} className={cn("shrink-0", !filled && "text-[#1F1F1F]/12")} />);
    $[0] = size;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const row = t1;
  const t2 = `${value.toFixed(1)} из 5`;
  let t3;
  if ($[2] !== row) {
    t3 = <span className="flex gap-0.5">{row(false)}</span>;
    $[2] = row;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== className) {
    t4 = cn("absolute inset-y-0 left-0 flex gap-0.5 overflow-hidden", className);
    $[4] = className;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `${pct}%`;
  let t6;
  if ($[6] !== t5) {
    t6 = {
      width: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== row) {
    t7 = row(true);
    $[8] = row;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t6 || $[12] !== t7) {
    t8 = <span className={t4} style={t6}>{t7}</span>;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t8) {
    t9 = <span className="relative inline-flex" role="img" aria-label={t2}>{t3}{t8}</span>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}
function Stat(t0) {
  const $ = _c(7);
  const {
    value,
    label
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <span className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-black">{value}</span>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-[12px] leading-4 text-black">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex w-[65px] flex-col items-center gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}